import { useQuery } from "@tanstack/react-query";
import { Loader2, Percent } from "lucide-react";
import api from "@/lib/api";
import { money } from "@/lib/format";

const STATUS_CLS = {
  active: "bg-[#5C8A72]/15 text-[#5C8A72]", scheduled: "bg-[#144F75]/10 text-[#144F75]",
  expired: "bg-[#F1EDE6] text-[#8FA89B]", disabled: "bg-[#F1EDE6] text-[#8FA89B]",
  exhausted: "bg-[#E0A458]/20 text-[#B5794E]",
};
const KIND_LABEL = { coupon: "Coupon", membership: "Membership", promotion: "Promotion" };
const APPLIES_LABEL = { all: "Everything", services: "Services", events: "Events", merch: "Merch" };

export default function CouponsOverviewPanel() {
  const { data, isLoading } = useQuery({ queryKey: ["admin-coupons"], queryFn: () => api.get("/admin/coupons").then((r) => r.data) });
  const rows = data || [];
  return (
    <div data-testid="admin-coupons-overview">
      <div className="flex items-center justify-between mb-1 flex-wrap gap-2">
        <h2 className="font-serif text-2xl text-[#1A242B] flex items-center gap-2"><Percent className="text-[#144F75]" size={22} /> Coupons & Discounts</h2>
        <span className="text-xs text-[#8FA89B] max-w-[300px] text-right">Read-only overview — each center creates and manages its own discounts from the Center dashboard. No admin approval is required.</span>
      </div>
      <p className="text-sm text-[#4A5D6B] mb-5">{rows.length} discount{rows.length === 1 ? "" : "s"} across all centers.</p>
      {isLoading ? (
        <div className="p-8 text-center"><Loader2 className="animate-spin text-[#144F75] inline" /></div>
      ) : rows.length === 0 ? (
        <p className="text-sm text-[#4A5D6B] eyw-card p-6 text-center" data-testid="admin-coupons-empty">No coupons or discounts created yet.</p>
      ) : (
        <div className="rounded-xl border border-[#E5E0D8] bg-white overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="bg-[#F9F7F3] text-left text-xs uppercase tracking-wide text-[#8FA89B]">
                <th className="px-4 py-3">Center</th><th className="px-4 py-3">Name</th><th className="px-4 py-3">Type</th>
                <th className="px-4 py-3">Code</th><th className="px-4 py-3">Discount</th><th className="px-4 py-3">Applies to</th>
                <th className="px-4 py-3">Window</th><th className="px-4 py-3">Uses</th><th className="px-4 py-3">Status</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-[#E5E0D8]">
              {rows.map((c) => (
                <tr key={c.id} data-testid={`admin-coupon-${c.id}`}>
                  <td className="px-4 py-3 text-[#1A242B]">{c.location_name || "—"}</td>
                  <td className="px-4 py-3 text-[#1A242B] font-medium">{c.name}</td>
                  <td className="px-4 py-3 text-[#4A5D6B]">{KIND_LABEL[c.kind] || c.kind}{c.kind === "membership" && c.membership_tier ? ` (${c.membership_tier})` : ""}</td>
                  <td className="px-4 py-3 font-mono text-[#144F75]">{c.code || "—"}</td>
                  <td className="px-4 py-3 text-[#4A5D6B]">{c.discount_type === "percent" ? `${Number(c.value)}%` : `${money(Number(c.value))}`}</td>
                  <td className="px-4 py-3 text-[#4A5D6B]">{APPLIES_LABEL[c.applies_to] || c.applies_to}</td>
                  <td className="px-4 py-3 text-[#4A5D6B] whitespace-nowrap">{c.starts_at || "now"} → {c.expires_at || "∞"}</td>
                  <td className="px-4 py-3 text-[#4A5D6B]">{c.uses_count}{c.max_uses ? `/${c.max_uses}` : ""}</td>
                  <td className="px-4 py-3"><span className={`text-[10px] uppercase px-2 py-0.5 rounded-full ${STATUS_CLS[c.status] || STATUS_CLS.expired}`}>{c.status}</span></td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}
